import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import Modal from "./Modal";
import Login from "./Login";

const Icon = ({ path }) => (
  <svg className="w-4 h-4 text-custom-maroon flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

export default function NavBar({ onApplyClick }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  
  const location = useLocation();
  const megaMenuTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150); 
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setIsMenuOpen(false);
  }, [location.pathname, location.state]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const isActive = (path) => location.pathname === path;

  const handleMouseEnter = () => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
    }
    setIsMegaMenuOpen(true);
  };

  const handleMouseLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
    }, 150); 
  };

  const menuData = {
    home: [
      { label: "How To Apply", path: "/", state: { scrollTo: "howtoapply" }, icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
      { label: "What We Do", path: "/", state: { scrollTo: "whatwedo" }, icon: "M13 10V3L4 14h7v7l9-11h-7z" },
      { label: "Featured Articles", path: "/", state: { scrollTo: "articles" }, icon: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10l4 4v10a2 2 0 01-2 2z" },
      { label: "Featured Incubatees", path: "/", state: { scrollTo: "incubatees" }, icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
      { label: "Featured Programs", path: "/", state: { scrollTo: "programs" }, icon: "M12 14l9-5-9-5-9 5 9 5z" },
      { label: "Our Partners", path: "/", state: { scrollTo: "partners" }, icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" },
      { label: "About Us", path: "/", state: { scrollTo: "about" }, icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
      { label: "Mission & Vision", path: "/", state: { scrollTo: "missionvision" }, icon: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" },
      { label: "Our Team", path: "/", state: { scrollTo: "team" }, icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" },
    ],
    programs: [
      {
        category: "Pre-Incubation",
        items: [
          { label: "SINDI", path: "/programs", state: { tab: "pre-incubation", scrollTo: "programs-section" }, icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" },
          { label: "Call for Cohort III", path: "/programs", state: { tab: "pre-incubation", scrollTo: "programs-section" }, icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" }
        ]
      },
      {
        category: "Incubation",
        items: [
          { label: "Dagitab Program", path: "/programs", state: { tab: "incubation", scrollTo: "programs-section" }, icon: "M7 11l5-5m0 0l5 5m-5-5v12" }
        ]
      },
      {
        category: "Tech-Based",
        items: [
          { label: "Tech-Based Programs", path: "/programs", state: { tab: "tech-based", scrollTo: "programs-section" }, icon: "M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" }
        ]
      },
      {
        category: "Networking",
        items: [
          { label: "Pylon Awards: Night", path: "/programs", state: { tab: "networking", scrollTo: "programs-section" }, icon: "M12 14l9-5-9-5-9 5 9 5z" },
          { label: "Pylon Awards: Call for Nomination", path: "/programs", state: { tab: "networking", scrollTo: "programs-section" }, icon: "M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" }
        ]
      }
    ],
    incubatees: [
      { label: "Cohort 1", path: "/incubatees", state: { cohort: "cohort1", scrollTo: "cohorts-section" }, icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857" },
      { label: "Cohort 2", path: "/incubatees", state: { cohort: "cohort2", scrollTo: "cohorts-section" }, icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" },
    ]
  };

  return (
    <div className="relative">
      <nav
        className={`fixed top-4 left-1/2 -translate-x-1/2 w-[80%] max-w-6xl 2xl:max-w-5xl z-50
        ${isScrolled || isMenuOpen ? "bg-white shadow-md" : "bg-white/90"}
        rounded-md sm:rounded-full px-6 py-3 transition-all`}
      >
        <div className="flex items-center justify-between">

          {/* LOGO */}
          <Link to="/" onClick={closeMenu}>
            <img
             src="/images/tbi-logo.png" 
             alt="Logo" 
             className="w-28 cursor-pointer" 
             />
          </Link>

          {/* DESKTOP NAV */}
          <ul 
            className="hidden md:flex items-center gap-8 text-sm text-gray-700 py-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {["Home", "Programs", "Incubatees", "Blogs"].map((item) => (
              <li key={item}>
                <Link 
                  to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className={`hover:text-custom-maroon transition-colors ${isActive(item === "Home" ? "/" : `/${item.toLowerCase()}`) ? 'font-bold' : ''}`}
                >
                  {item}
                </Link>
              </li>
            ))}
            <li>
              <a 
              href="https://tfa.cybernestsolution.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-custom-maroon"
              >
                Co-Working
              </a>
            </li>
          </ul>

          {/* DESKTOP BUTTONS */}
          <div className="hidden md:flex items-center text-sm gap-3">
            <button 
            onClick={() => setIsLoginModalOpen(true)} 
            className="rounded-full px-5 py-1.5 border border-gray-400 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              View Status
            </button>

            <button 
            onClick={onApplyClick} 
            className="rounded-full px-5 py-1.5 border border-custom-maroon text-custom-maroon hover:bg-custom-maroon hover:text-white transition-colors"
            >
              Apply Now
            </button>
          </div>

          {/* MOBILE MENU BUTTON */}
          <button className="md:hidden p-2 text-gray-700" onClick={toggleMenu} aria-label="Toggle Menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>

        {/* MEGA MENU DROPDOWN (DESKTOP) */}
        {isMegaMenuOpen && (
          <div 
            className="hidden md:block absolute top-full left-0 right-0 pt-2"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div 
              className="bg-white shadow-xl rounded-2xl p-8 grid grid-cols-3 gap-8 border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300"
            >
              {/* Home Shortcuts */}
              <div>
                <h4 className="text-custom-maroon font-bold text-xs uppercase tracking-wider mb-4 border-b pb-2">Quick Navigation</h4>
                <ul className="space-y-3">
                  {menuData.home.map((link) => (
                    <li key={link.label}>
                      <Link to={link.path} state={link.state} className="flex items-center gap-3 text-gray-600 hover:text-custom-maroon transition-all group" onClick={() => setIsMegaMenuOpen(false)}>
                        <Icon path={link.icon} />
                        <span className="text-sm group-hover:translate-x-1 transition-transform">{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Programs Shortcuts */}
              <div>
                <h4 className="text-custom-maroon font-bold text-xs uppercase tracking-wider mb-4 border-b pb-2">Our Programs</h4>
                <div className="space-y-4">
                  {menuData.programs.map((group) => (
                    <div key={group.category}>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">{group.category}</span>
                      <ul className="space-y-3">
                        {group.items.map((link) => (
                          <li key={link.label}>
                            <Link to={link.path} state={link.state} className="flex items-center gap-3 text-gray-600 hover:text-custom-maroon transition-all group" onClick={() => setIsMegaMenuOpen(false)}>
                              <Icon path={link.icon} />
                              <span className="text-sm group-hover:translate-x-1 transition-transform leading-tight">{link.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Incubatees Shortcuts */}
              <div className="flex flex-col h-full">
                <h4 className="text-custom-maroon font-bold text-xs uppercase tracking-wider mb-4 border-b pb-2">Startup Cohorts</h4>
                <ul className="space-y-3 mb-6">
                  {menuData.incubatees.map((link) => (
                    <li key={link.label}>
                      <Link to={link.path} state={link.state} className="flex items-center gap-3 text-gray-600 hover:text-custom-maroon transition-all group" onClick={() => setIsMegaMenuOpen(false)}>
                        <Icon path={link.icon} />
                        <span className="text-sm group-hover:translate-x-1 transition-transform">{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                
                {/* Stay tuned for Cohort 3 Notice */}
                <div className="flex-grow flex flex-col justify-center items-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200 p-6 text-center">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 mb-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">Stay tuned for Cohort 3</span>
                  <p className="text-xs text-gray-500 mt-1">More updates coming soon</p>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* MOBILE DROPDOWN MENU */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 mt-2 bg-white shadow-xl rounded-2xl p-6 border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300 max-h-[75vh] overflow-y-auto">
            
            {/* Primary Navigation */}
            <ul className="flex flex-col gap-4 mb-6 border-b border-gray-100 pb-6">
              {["Home", "Programs", "Incubatees", "Blogs"].map((item) => (
                <li key={item}>
                  <Link 
                    to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                    className={`text-lg transition-colors ${isActive(item === "Home" ? "/" : `/${item.toLowerCase()}`) ? 'text-custom-maroon font-bold' : 'text-gray-700 hover:text-custom-maroon'}`}
                    onClick={closeMenu}
                  >
                    {item}
                  </Link>
                </li>
              ))}
              <li>
                <a 
                href="https://tfa.cybernestsolution.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-lg text-gray-700 hover:text-custom-maroon transition-colors"
                onClick={closeMenu}
                >
                  Co-Working
                </a>
              </li>
            </ul>

            {/* Mobile Quick Navigation */}
            <div className="mb-6 border-b border-gray-100 pb-6">
              <h4 className="text-custom-maroon font-bold text-xs uppercase tracking-wider mb-4">Quick Navigation</h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {menuData.home.map((link) => (
                  <li key={link.label}>
                    <Link to={link.path} state={link.state} className="flex items-center gap-3 text-gray-600 hover:text-custom-maroon" onClick={closeMenu}>
                      <Icon path={link.icon} />
                      <span className="text-sm">{link.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Mobile Programs Navigation */}
            <div className="mb-6 border-b border-gray-100 pb-6">
              <h4 className="text-custom-maroon font-bold text-xs uppercase tracking-wider mb-4">Our Programs</h4>
              <div className="space-y-4">
                {menuData.programs.map((group) => (
                  <div key={group.category}>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">{group.category}</span>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {group.items.map((link) => (
                        <li key={link.label}>
                          <Link to={link.path} state={link.state} className="flex items-start gap-3 text-gray-600 hover:text-custom-maroon" onClick={closeMenu}>
                            <div className="mt-0.5"><Icon path={link.icon} /></div>
                            <span className="text-sm leading-tight">{link.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Actions */}
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => { setIsLoginModalOpen(true); closeMenu(); }} 
                className="w-full rounded-full px-5 py-3 border border-gray-400 text-gray-700 hover:bg-gray-100 font-medium transition-colors"
              >
                View Status
              </button>

              <button 
                onClick={() => { onApplyClick(); closeMenu(); }} 
                className="w-full rounded-full px-5 py-3 border border-custom-maroon bg-custom-maroon text-white hover:bg-opacity-90 font-medium transition-colors"
              >
                Apply Now
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* MODALS */}
      <Modal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)}>
        <Login />
      </Modal>
    </div>
  );
}